import { useEffect, useState } from "react";
import {
  fetchLogInsightsList,
  type LogInsightsListParams,
} from "../api/logInsightsApi";
import type { LogInsightRole, LogInsightRow } from "../app/types/logReview";

export interface UseLogInsightsListArgs {
  role: LogInsightRole;
  pageNumber: number;
  itemsPerPage: number;
  keyword: string;
  sortByColumn: string;
  sortOrder: "asc" | "desc" | "";
  appCode: string[];
  category: string[];
  division: string[];
  teams: string[];
  risk: string[];
  status: string[];
  criticality: string[];
  startDate: string;
  endDate: string;
  /** Bumped externally to force a refetch (top filter apply / decision). */
  reloadKey?: number;
  enabled?: boolean;
}

export interface UseLogInsightsListResult {
  rows: LogInsightRow[];
  totalCount: number;
  canApprove: boolean;
  loading: boolean;
  error: string | null;
}

/**
 * Server-driven Details list for the Insights pages. Re-fetches on every
 * page / size / keyword / sort / filter change and aborts the in-flight
 * request on unmount or input change.
 */
export const useLogInsightsList = (
  args: UseLogInsightsListArgs,
): UseLogInsightsListResult => {
  const {
    role,
    pageNumber,
    itemsPerPage,
    keyword,
    sortByColumn,
    sortOrder,
    appCode,
    category,
    division,
    teams,
    risk,
    status,
    criticality,
    startDate,
    endDate,
    reloadKey = 0,
    enabled = true,
  } = args;

  const [rows, setRows] = useState<LogInsightRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [canApprove, setCanApprove] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stable primitive key — re-runs only when a real input changes.
  const key = [
    role,
    pageNumber,
    itemsPerPage,
    keyword,
    sortByColumn,
    sortOrder,
    appCode.join(","),
    category.join(","),
    division.join(","),
    teams.join(","),
    risk.join(","),
    status.join(","),
    criticality.join(","),
    startDate,
    endDate,
    reloadKey,
    enabled,
  ].join("|");

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(true);
    setError(null);

    const params: LogInsightsListParams = {
      keyword,
      sortByColumn,
      sortOrder,
      appCode,
      category,
      division,
      teams,
      risk,
      status,
      criticality,
      startDate,
      endDate,
    };

    fetchLogInsightsList(role, params, pageNumber, itemsPerPage, signal)
      .then((res) => {
        if (signal.aborted) return;
        setRows(res.rows);
        setTotalCount(res.totalCount);
        setCanApprove(res.canApprove);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (signal.aborted) return;
        setError(err instanceof Error ? err.message : "Failed to load list");
        setLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { rows, totalCount, canApprove, loading, error };
};
